import { Shield, Award } from "lucide-react";
import GoogleReviewBadge from "@/components/GoogleReviewBadge";
import { CREDENTIALS } from "@/config/credentials";
import { cn } from "@/lib/utils";

interface TrustBarProps {
  /** Visual variant. `hero-dark` = light chips on dark hero; `hero-light` = subtle chips on light; `inline` = neutral inline row. */
  variant?: "hero-dark" | "hero-light" | "inline";
  /** Hide the Google Reviews badge (e.g. when the hero already renders it). */
  hideReviews?: boolean;
  /** Hide the founded-year chip. */
  hideYear?: boolean;
  /** Extra classes on the outer wrapper. */
  className?: string;
}

/**
 * TrustBar
 * --------
 * Above-the-fold credibility strip: Google reviews + FINMA + CICERO + Since 2013.
 * Uses semantic tokens; safe over dark heros with `variant="hero-dark"`.
 */
const TrustBar = ({
  variant = "hero-dark",
  hideReviews = false,
  hideYear = false,
  className,
}: TrustBarProps) => {
  const chipBase =
    "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium min-h-[32px] whitespace-nowrap";
  const chipVariant =
    variant === "hero-dark"
      ? "bg-white/10 backdrop-blur-sm border border-white/20 text-white"
      : variant === "hero-light"
        ? "bg-foreground/5 border border-foreground/10 text-foreground"
        : "bg-muted border border-border text-foreground";

  const foundedYears = new Date().getFullYear() - CREDENTIALS.foundedYear;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 sm:gap-2.5",
        className,
      )}
      aria-label="Credenciales oficiales y reseñas"
    >
      {!hideReviews && <GoogleReviewBadge />}

      <a
        href={CREDENTIALS.finma.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(chipBase, chipVariant, "hover:opacity-90 transition-opacity")}
        title={`Registro ${CREDENTIALS.finma.label} N.° ${CREDENTIALS.finma.number}`}
      >
        <Shield className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="font-semibold">{CREDENTIALS.finma.label}</span>
        <span className="opacity-80">N.° {CREDENTIALS.finma.number}</span>
      </a>

      <a
        href={CREDENTIALS.cicero.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(chipBase, chipVariant, "hover:opacity-90 transition-opacity")}
        title={`Registro ${CREDENTIALS.cicero.label} N.° ${CREDENTIALS.cicero.number}`}
      >
        <Award className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="font-semibold">{CREDENTIALS.cicero.label}</span>
        <span className="opacity-80">N.° {CREDENTIALS.cicero.number}</span>
      </a>

      {!hideYear && (
        <span className={cn(chipBase, chipVariant)}>
          <span aria-hidden="true">🇨🇭</span>
          <span className="font-semibold">{CREDENTIALS.swissLabel}</span>
          <span className="opacity-80">· {foundedYears}+ años</span>
        </span>
      )}
    </div>
  );
};

export default TrustBar;
